import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { studentGradesQueryKeyPrefix } from '@/features/gradebook/useStudentGrades';
import { buildUpdateComponentSettingsPayload } from '@/features/content/control-panel/hooks/useUpdateComponentSettings';
import type {
  ComponentPageData,
  RawComponent,
  RawQuestionSettings,
} from '@/features/content/useComponent';

import { statsFailure } from './statsFailure';

// Which question set to regrade.
export type RegradeSection = 'homework' | 'preLecture';

// How points are awarded. Maps to the backend's participation-credit settings,
// which `scoreQuestion` interprets:
//   - correctness:  full points only for a correct answer.
//   - participation: full points for any answer (correct or not).
//   - both: one point for answering PLUS one more for a correct answer
//     (participation weight 1 + correctness).
export type RegradeScheme = 'correctness' | 'participation' | 'both';

export interface RegradeQuestionsInput {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  scheme: RegradeScheme;
  section: RegradeSection;
}

/**
 * The participation-credit settings the backend needs for a given scheme. This is
 * the exact mapping the old frontend used, and it is what `scoreQuestion` reads:
 *   correctness  -> credit off, weight 0   (1.0 iff correct)
 *   participation-> credit on              (1.0 for any answer)
 *   both         -> credit off, weight 1.0 (1.0 for answering + 1.0 if correct)
 */
export function regradeParticipationSettings(scheme: RegradeScheme): {
  all_questions_participation_credit: boolean;
  all_questions_participation_credit_weight: number;
} {
  switch (scheme) {
    case 'participation':
      return {
        all_questions_participation_credit: true,
        all_questions_participation_credit_weight: 0,
      };
    case 'both':
      return {
        all_questions_participation_credit: false,
        all_questions_participation_credit_weight: 1,
      };
    case 'correctness':
    default:
      return {
        all_questions_participation_credit: false,
        all_questions_participation_credit_weight: 0,
      };
  }
}

/**
 * Apply the regrade grading scheme onto the draft component's settings, preserving
 * every other setting (assignment type, user-defined numbers, attempt limits, ...)
 * and only overwriting the two participation-credit fields. Writes both the
 * snake_case and camelCase spellings so downstream readers agree. Homework uses
 * `question_settings`; pre-lecture uses `pre_lecture_question_settings`.
 */
export function applyRegradeSettings(
  draft: RawComponent,
  section: RegradeSection,
  scheme: RegradeScheme,
): void {
  const participation = regradeParticipationSettings(scheme);

  if (section === 'preLecture') {
    const existing: RawQuestionSettings =
      draft.pre_lecture_question_settings ??
      draft.preLectureQuestionSettings ??
      {};
    const next: RawQuestionSettings = { ...existing, ...participation };
    draft.pre_lecture_question_settings = next;
    draft.preLectureQuestionSettings = next;
    return;
  }

  const existing: RawQuestionSettings =
    draft.question_settings ?? draft.questionSettings ?? {};
  const next: RawQuestionSettings = { ...existing, ...participation };
  draft.question_settings = next;
  draft.questionSettings = next;
}

export interface RegradeGradePayload {
  component_id: string;
  course_id: string;
  grade_component: true;
  is_instructor_request: true;
  only_score_pre_lecture: boolean;
  user?: NonNullable<ComponentPageData['instructorUser']>;
  user_id: string;
}

/**
 * The grade request that re-scores every student. `is_instructor_request` routes
 * to AddOrUpdateCourseInstructorComponent, which fetches all students and, because
 * `grade_component` is true, re-scores each against the component's (just-updated)
 * settings. `only_score_pre_lecture` targets the pre-lecture question set.
 */
export function buildRegradeGradePayload(
  componentId: string,
  courseId: string,
  section: RegradeSection,
  userId: string,
  instructorUser?: ComponentPageData['instructorUser'],
): RegradeGradePayload {
  return {
    component_id: componentId,
    course_id: courseId,
    grade_component: true,
    is_instructor_request: true,
    only_score_pre_lecture: section === 'preLecture',
    user_id: userId,
    ...(instructorUser ? { user: instructorUser } : {}),
  };
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

/**
 * Regrades every student's answers for a component's homework or pre-lecture
 * questions under the chosen grading scheme. Two sequential RPCs — the order
 * matters: the settings MUST be persisted onto the core component first, because
 * the grade pass re-scores students against the core component's settings.
 *   1. UpdateCourse — persist the participation-credit scheme onto the component.
 *   2. AddOrUpdateCourseStudentComponentV2 (instructor + grade) — re-score all
 *      students against those settings.
 */
export function useRegradeQuestions() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, RegradeQuestionsInput>({
    mutationFn: async ({ component, componentId, courseId, scheme, section }) => {
      if (!user) {
        throw createDataError('You must be signed in to regrade questions.');
      }
      const kuduUserId = getKuduUserId(user) ?? user.uid;
      if (!kuduUserId) {
        throw createDataError('Unable to determine the current Kudu user ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      // 1. Persist the grading scheme onto the core component.
      const settingsPayload = buildUpdateComponentSettingsPayload(
        component,
        courseId,
        (draft) => applyRegradeSettings(draft, section, scheme),
      );
      // 2. Re-score every student against the just-updated settings.
      const gradePayload = buildRegradeGradePayload(
        componentId,
        courseId,
        section,
        kuduUserId,
        component.instructorUser,
      );
      try {
        await client.invokeKuduMethod<
          unknown | LegacyKuduResponseEnvelope<unknown>,
          typeof settingsPayload
        >('UpdateCourse', settingsPayload);
        await client.invokeKuduMethod<
          unknown | LegacyKuduResponseEnvelope<unknown>,
          RegradeGradePayload
        >('AddOrUpdateCourseStudentComponentV2', gradePayload);
      } catch (error) {
        throw statsFailure(error, 'The scores were not regraded.');
      }
    },
    onSuccess: async (_data, variables) => {
      // Every student's scores may have changed: the gradebook must not serve
      // its cached grades (kept for up to a minute).
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['component', variables.courseId, variables.componentId],
        }),
        queryClient.invalidateQueries({
          queryKey: studentGradesQueryKeyPrefix(variables.courseId),
        }),
      ]);
    },
  });
}
